import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { formatDateOnly, THERAPY_DISCIPLINES, toDateInput, errorWords } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { WorkspaceBack } from "@/components/app/workspace/workspace-back";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { putToSignedUrl } from "@/lib/files/signed-upload";
import { useCan } from "@/lib/policy";
import { DocumentField } from "./-components/therapy/therapy-document-field";
import { ReferralForm, type ReferralValues } from "./-components/therapy/therapy-referral-form";
import { ReferralDiagnoses } from "./-components/chart/episode-diagnoses";
import { PhysicianSignatureCard } from "./-components/therapy/therapy-physician-signature";
import { openLink } from "@/lib/links/in-app";

export const Route = createFileRoute(
  "/_authenticated/_care/residents/$residentId/therapy-referrals/$referralId",
)({
  component: TherapyReferralPage,
});

const fail = (error: Error) => toast.error(errorWords(error));
const disciplineLabel = (value: string) =>
  THERAPY_DISCIPLINES.find((row) => row.value === value)?.label ?? value;
/** The physician's order that authorizes a course of therapy: read it, amend it while it is still a
 * draft, send it for signature, and file the page that comes back. */
function TherapyReferralPage() {
  const { referralId, residentId } = Route.useParams();
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [removingScan, setRemovingScan] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const referral = useQuery(
    orpc.care.therapy.referrals.get.queryOptions({ input: { referralId } }),
  );
  const physicians = useQuery(
    orpc.care.directories.providers.list.queryOptions({
      input: { facilityId, includeInactive: false },
    }),
  );

  const canChart = useCan("therapy:chart", referral.data?.facility?.id ?? null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.care.therapy.key() });
  const done = (message: string) => async () => {
    toast.success(message);
    setEditing(false);
    await invalidate();
  };

  const update = useMutation(
    orpc.care.therapy.referrals.update.mutationOptions({
      onSuccess: done("Referral saved"),
      onError: fail,
    }),
  );
  const requestSignature = useMutation(
    orpc.care.therapy.referrals.requestSignature.mutationOptions({
      onSuccess: done("Sent to the physician"),
      onError: fail,
    }),
  );
  const removeScan = useMutation(
    orpc.care.therapy.referrals.signedDocument.remove.mutationOptions({
      onSuccess: done("Signed page removed"),
      onError: fail,
    }),
  );
  const download = useMutation(
    orpc.care.therapy.referrals.signedDocument.downloadUrl.mutationOptions({
      onSuccess: (result) => openLink(result.url),
      onError: fail,
    }),
  );

  const fileScan = async (file: File) => {
    try {
      const contentType = file.type || "application/pdf";
      const target = await orpc.care.therapy.referrals.signedDocument.uploadUrl.call({
        referralId,
        contentType,
      });
      await putToSignedUrl(target.url, file, contentType, "The file did not upload.");
      await orpc.care.therapy.referrals.signedDocument.attach.call({
        referralId,
        filePath: target.objectPath,
        fileName: file.name,
      });
      toast.success("Signed page filed");
      await invalidate();
    } catch (error) {
      fail(error instanceof Error ? error : new Error("The signed page was not filed."));
    }
  };

  /* Built before the referral is, and rendered even when there is none: one that is slow, deleted
   * or outside this caller's scope otherwise leaves a grey rectangle with nothing on the page to
   * click. */
  const back = (
    <WorkspaceBack
      fallback={
        <Button asChild variant="outline">
          <Link to="/residents/$residentId/therapies" params={{ residentId }}>
            Back to the chart
          </Link>
        </Button>
      }
    />
  );

  if (referral.isError)
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-2">{back}</div>
        <ReadFailed
          message="Could not load this referral."
          error={referral.error}
          onRetry={() => void referral.refetch()}
          retrying={referral.isFetching}
        />
      </div>
    );
  if (referral.isPending || !referral.data)
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-2">{back}</div>
        <Skeleton className="h-96 w-full" />
      </div>
    );

  const data = referral.data;
  const eSigned = Boolean(data.physicianSignedAt);
  const faxSigned = Boolean(data.physicianSignedDocPath);
  const waiting = data.physicianSignatureRequest;
  const physician = data.physician;

  return (
    <div className="space-y-6">
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void fileScan(file);
        }}
      />

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {disciplineLabel(data.track.discipline)} referral
            {eSigned ? (
              <Badge variant="ok">Physician e-signed</Badge>
            ) : faxSigned ? (
              <Badge variant="ok">Physician signed (fax)</Badge>
            ) : (
              <Badge variant="outline">Unsigned</Badge>
            )}
          </span>
        }
        meta={
          <>
            Referral date {data.referralDate ? formatDateOnly(data.referralDate) : "not recorded"}
            {data.facility?.name ? `, ${data.facility.name}` : ""}
          </>
        }
        actions={
          <>
            {back}
            {canChart ? (
              <>
                {!data.signed ? (
                  <Button variant="outline" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                ) : null}
                {!data.signed && !waiting ? (
                  <Button
                    variant="outline"
                    disabled={requestSignature.isPending}
                    onClick={() => requestSignature.mutate({ referralId })}
                  >
                    Send for signature
                  </Button>
                ) : null}
                {!eSigned ? (
                  <Button variant="outline" onClick={() => fileInput.current?.click()}>
                    {faxSigned ? "Replace signed page" : "File signed page"}
                  </Button>
                ) : null}
                {faxSigned ? (
                  <Button
                    variant="outline"
                    disabled={download.isPending}
                    onClick={() => download.mutate({ referralId })}
                  >
                    Open signed page
                  </Button>
                ) : null}
                {faxSigned && !eSigned ? (
                  <Button
                    variant="destructive"
                    disabled={removeScan.isPending}
                    onClick={() => setRemovingScan(true)}
                  >
                    Remove page
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Referral details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <DocumentField
            label="Ordering physician"
            value={physician ? `${physician.firstName} ${physician.lastName}` : null}
          />
          <DocumentField label="Discipline" value={disciplineLabel(data.discipline)} />
          <DocumentField label="Reason" value={data.reason} />
          <DocumentField label="Diagnosis" value={data.diagnosis} />
          <DocumentField
            className="sm:col-span-2"
            label="Requested services"
            value={data.requestedServices}
          />
          <DocumentField className="sm:col-span-2" label="Notes" value={data.notes} />
        </CardContent>
      </Card>

      {/* The medical-necessity section of the printed order, which nothing
          could fill in. */}
      <Card>
        <CardContent className="p-4">
          <ReferralDiagnoses residentId={residentId} referralId={referralId} canChart={canChart} />
        </CardContent>
      </Card>

      <PhysicianSignatureCard record={data} pendingSince={waiting?.requestedAt ?? null} />

      {removingScan ? (
        <RemoveConfirm
          title="Remove the signed page?"
          confirmLabel="Remove"
          description="The referral goes back to unsigned, waiting on the physician again, and can be edited."
          onConfirm={() => removeScan.mutate({ referralId })}
          onClose={() => setRemovingScan(false)}
        />
      ) : null}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit referral</DialogTitle>
          </DialogHeader>
          <ReferralForm
            physicians={physicians.data ?? []}
            pending={update.isPending}
            defaultValues={{
              referralId,
              physicianId: data.physicianId,
              referralDate: toDateInput(data.referralDate, null),
              reason: data.reason,
              diagnosis: data.diagnosis,
              requestedServices: data.requestedServices,
              notes: data.notes,
            }}
            onCancel={() => setEditing(false)}
            onSubmit={(values: ReferralValues) => update.mutate(values)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
